import { useState, useCallback, useEffect } from 'react'
import { useToast, toast as originalToast } from '@/hooks/use-toast'

export interface ToastHistoryItem {
  id: string
  title?: string
  description?: string
  variant?: 'default' | 'destructive'
  timestamp: Date
  action?: any
}

// Global toast history store
let globalToastHistory: ToastHistoryItem[] = []
let historyListeners: Array<(history: ToastHistoryItem[]) => void> = []

// Enhanced toast function that captures history
export const toastWithHistory = (props: Parameters<typeof originalToast>[0]) => {
  const toastId = Math.random().toString(36).substr(2, 9)
  
  // Create history item
  const historyItem: ToastHistoryItem = {
    id: toastId,
    title: typeof props.title === 'string' ? props.title : undefined,
    description: typeof props.description === 'string' ? props.description : undefined,
    variant: props.variant || 'default',
    timestamp: new Date(),
    action: props.action
  }
  
  // Add to global history
  globalToastHistory = [historyItem, ...globalToastHistory].slice(0, 50) // Keep last 50
  
  // Notify all listeners
  historyListeners.forEach(listener => listener(globalToastHistory))
  
  // Call original toast
  return originalToast(props)
}

export function useToastHistory() {
  const [history, setHistory] = useState<ToastHistoryItem[]>(globalToastHistory)
  
  useEffect(() => {
    const listener = (newHistory: ToastHistoryItem[]) => {
      setHistory([...newHistory])
    }
    
    historyListeners.push(listener)
    
    return () => {
      const index = historyListeners.indexOf(listener)
      if (index > -1) {
        historyListeners.splice(index, 1)
      }
    }
  }, [])
  
  const clearHistory = useCallback(() => {
    globalToastHistory = []
    historyListeners.forEach(listener => listener(globalToastHistory))
  }, [])
  
  const getTimeAgo = useCallback((timestamp: Date) => {
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - timestamp.getTime()) / 1000)
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s ago`
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
      return `${diffInHours}h ago`
    }
    
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}d ago`
  }, [])
  
  return {
    history,
    clearHistory,
    getTimeAgo,
    hasUnreadToasts: history.length > 0
  }
}

// Export enhanced toast for use throughout the app
export { toastWithHistory as toast }